import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Save, Image as ImageIcon, IndianRupee, CheckCircle2 } from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";

export default function VendorProfile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/partner/profile`).then(r => {
      setData(r.data);
      setForm(r.data.profile || {});
    }).catch(() => setData("err"));
  }, []);

  if (!user) return null;
  if (data === null) return <div className="text-ink-soft">Loading…</div>;
  if (data === "err") return <div className="text-ink-soft">No vendor profile. Visit <a href="/app/partner" className="text-brand font-bold">Partner</a> to set one up first.</div>;

  const role = data.role;
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDone(false); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name, bio: form.bio, photo: form.photo, cover_photo: form.cover_photo,
        phone: form.phone, contact_email: form.contact_email, city: form.city, address: form.address, hours: form.hours,
      };
      if (role === "vet") { payload.fee = Number(form.fee) || 0; payload.specialty = form.specialty; }
      if (role === "hostel_owner") { payload.price_per_day = Number(form.price_per_day) || 0; payload.amenities = form.amenities; }
      await axios.patch(`${API}/partner/profile`, payload);
      setDone(true);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="vendor-profile-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">Your business profile</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Edit details</h1>
        <p className="text-ink-soft text-sm">This is what pet parents see when they discover you. Keep it fresh.</p>
      </header>

      <form onSubmit={save} className="bg-surface border border-line rounded-3xl p-5 shadow-soft space-y-4" data-testid="profile-form">
        <Field label="Business name *"><input required value={form.name || ""} onChange={e => set("name", e.target.value)} className="li" data-testid="vp-name"/></Field>
        <Field label="Bio"><textarea rows={3} value={form.bio || ""} onChange={e => set("bio", e.target.value)} className="li" data-testid="vp-bio" placeholder="Tell pet parents what makes you special..."/></Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone"><input value={form.phone || ""} onChange={e => set("phone", e.target.value)} className="li"/></Field>
          <Field label="Contact email"><input type="email" value={form.contact_email || ""} onChange={e => set("contact_email", e.target.value)} className="li"/></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="City"><input value={form.city || ""} onChange={e => set("city", e.target.value)} className="li"/></Field>
          <Field label="Hours"><input value={form.hours || ""} onChange={e => set("hours", e.target.value)} className="li" placeholder="Mon-Sat · 10 AM – 8 PM"/></Field>
        </div>
        <Field label="Address"><input value={form.address || ""} onChange={e => set("address", e.target.value)} className="li"/></Field>

        {role === "vet" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Consultation fee (₹)"><input type="number" min="0" value={form.fee || 0} onChange={e => set("fee", e.target.value)} className="li" data-testid="vp-fee"/></Field>
            <Field label="Specialty"><input value={form.specialty || ""} onChange={e => set("specialty", e.target.value)} className="li" placeholder="General / Surgery / Dermatology"/></Field>
          </div>
        )}
        {role === "hostel_owner" && (
          <Field label="Price per day (₹)"><input type="number" min="0" value={form.price_per_day || 0} onChange={e => set("price_per_day", e.target.value)} className="li"/></Field>
        )}

        <Field label="Profile photo">
          <PhotoUpload value={form.photo} onChange={(b64) => set("photo", b64)} testid="vp-photo"/>
        </Field>

        <button disabled={saving} data-testid="vp-save" className="w-full bg-brand text-white rounded-full py-3 font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2">
          <Save className="w-4 h-4"/>{saving ? "Saving…" : "Save changes"}
        </button>
        {done && <div className="text-center text-sm text-brand-green font-bold inline-flex items-center justify-center w-full gap-1"><CheckCircle2 className="w-4 h-4"/> Saved</div>}
        <style>{`.li{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.li:focus{border-color:#FF6B35;}`}</style>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
